import {createUpdateConversationsTrackerStatusTask} from '../../../../srcCommon/tasks/conversations/updateConversationsTrackerStatusTask';
import {
  buildConversationUpdater,
  BumpedAtFinder,
  ConversationActionContextFinder,
  ConversationStatusesEnum,
  ConversationUpdater,
  setConversationReminder,
  setConversationStatus,
} from '../../../helpers/models/conversationHelpers';
import {shouldUseTeamPatch} from '../../../helpers/models/conversationPatchHelpers';
import {
  BumpedAtTimestamp,
  ConversationActionContextsEnum,
  ConversationId,
  ConversationTeamStatusesEnum,
} from '../../../models/api/conversationCommonTypes';
import {ConversationPatch} from '../../../models/api/conversationPatchModel';
import {ConversationTrackerStatusesEnum} from '../../../models/api/conversationTrackerModel';
import {StateGetter} from '../../appStore';
import {ThunkAsyncAction} from '../../appThunk';
import {ConversationUndoBannerTypesEnum} from '../../panels/app/banners/bannersState';
import {CellsContext} from '../../panels/cells/context/cellsContext';
import {InboxesParameters} from '../../panels/inboxes/parameters/inboxesParameters';
import {updateConversationsBulkAction} from './bulkConversationsActions';
import {updateConversationsAction} from './updateConversationsActions';

interface BumpConversationOptions {
  // TODO: Make it a function ala findConversationActionContext.
  previousBumpedAt: Record<ConversationId, BumpedAtTimestamp | undefined>;
  preventUndo?: boolean;
}

export function setConversationsReminderAction(
  cellsContext: CellsContext,
  conversationIds: ReadonlyArray<number>,
  remindAt: number | null,
  findConversationActionContext: ConversationActionContextFinder,
  bumpOptions?: BumpConversationOptions,
): ThunkAsyncAction {
  const previousBumpedAt = (bumpOptions && bumpOptions.previousBumpedAt) || {};
  const findBumpedAt =
    bumpOptions && bumpOptions.previousBumpedAt
      ? (conversationId: ConversationId) => bumpOptions.previousBumpedAt[conversationId]
      : undefined;

  return async (dispatch, getState) => {
    const offlineTask = maybeCreateOfflineTask(
      getState,
      cellsContext,
      conversationIds,
      remindAt,
      findBumpedAt,
    );

    const options = {
      cellsContext,
      offlineTask,
      undo: bumpOptions?.preventUndo
        ? undefined
        : {
            type: ConversationUndoBannerTypesEnum.REMINDER,
          },
    };

    await dispatch(
      updateConversationsAction(
        conversationIds,
        (conversationId) => {
          const conversationActionContext = findConversationActionContext(conversationId);

          return (
            conversationActionContext &&
            buildConversationPatch(
              cellsContext,
              conversationActionContext,
              remindAt,
              previousBumpedAt[conversationId],
            )
          );
        },
        setConversationReminderAndStatus(cellsContext, remindAt, findBumpedAt),
        options,
      ),
    );
  };
}

export function bulkSetConversationsReminderAction(
  inboxesParameters: InboxesParameters,
  cellsContext: CellsContext,
  remindAt: number | null,
): ThunkAsyncAction {
  const conversationPatch = buildConversationPatch(
    cellsContext,
    shouldUseTeamPatch(cellsContext)
      ? ConversationActionContextsEnum.TEAM
      : ConversationActionContextsEnum.TEAMMATE,
    remindAt,
  );
  return updateConversationsBulkAction(inboxesParameters, cellsContext, conversationPatch);
}

function maybeCreateOfflineTask(
  getState: StateGetter,
  cellsContext: CellsContext,
  conversationIds: ReadonlyArray<number>,
  remindAt: number | null,
  findBumpedAt?: BumpedAtFinder,
) {
  return createUpdateConversationsTrackerStatusTask(getState, cellsContext, conversationIds, {
    status: computeTrackerStatusForReminder(remindAt),
    findBumpedAt,
    remindAt,
  });
}

function computeTrackerStatusForReminder(remindAt: number | null) {
  // TODO: Should we be using isNull(remindAt) everywhere in this module?
  return remindAt ? ConversationTrackerStatusesEnum.ARCHIVED : ConversationTrackerStatusesEnum.INBOX;
}

function buildConversationPatches(
  cellsContext: CellsContext,
  remindAt: number | null,
  previousBumpedAt?: BumpedAtTimestamp,
) {
  const {teammateId} = cellsContext;

  // Build the tracker and team patches.
  const trackerPatch: ConversationPatch = {
    addTrackers: [
      {
        teammateId,
        remindAt,
        status: computeTrackerStatusForReminder(remindAt),
        bumpedAt: previousBumpedAt,
      },
    ],
  };

  const teamPatch: ConversationPatch = {
    remindAt,
    teamStatus: remindAt ? ConversationTeamStatusesEnum.ARCHIVED : ConversationTeamStatusesEnum.OPEN,
  };

  return {trackerPatch, teamPatch};
}

function buildConversationPatch(
  cellsContext: CellsContext,
  conversationContext: ConversationActionContextsEnum,
  remindAt: number | null,
  previousBumpedAt?: BumpedAtTimestamp,
) {
  const {trackerPatch, teamPatch} = buildConversationPatches(cellsContext, remindAt, previousBumpedAt);

  // If we're the assignee, apply both.
  if (conversationContext === ConversationActionContextsEnum.ASSIGNEE) {
    return {
      ...trackerPatch,
      ...teamPatch,
    };
  }

  // Otherwise, it depends on whether we're in a team context or not.
  return conversationContext === ConversationActionContextsEnum.TEAM ? teamPatch : trackerPatch;
}

function setConversationReminderAndStatus(
  cellsContext: CellsContext,
  remindAt: number | null,
  findBumpedAt: BumpedAtFinder | undefined,
): ConversationUpdater {
  const newStatus = remindAt ? ConversationStatusesEnum.ARCHIVED : ConversationStatusesEnum.OPEN;
  return buildConversationUpdater(
    setConversationReminder(cellsContext, remindAt),
    setConversationStatus(cellsContext, newStatus, findBumpedAt),
  );
}
